<template>
  <timeline-step
    :ref="`step-${step.id}`"
    :step="step"
    :dusk="`timeline-step-${step.id}`"
    @toggleStep="toggleStep(step.id)"
  >
    <div class="my-4 text-xl">
      <div
        v-if="applied"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-gray-600 font-bold text-sm"
      >
        Thank you for your application. We will review it and get back to you soon.
      </div>
      <div
        v-else-if="joined && contentApprovalStatusIsPending"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-blue-600 font-bold text-sm"
      >
        👌 Thank you for submitting. We'll get back to you as soon as your content gets reviewed.
      </div>
      <div
        v-else-if="joined && campaignHasCreativeAssetsObjective && hasApprovedContent"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-green-600 font-bold text-sm"
      >
        👌 Thank you for submitting. Feel free to join other campaigns.
      </div>
      <div
        v-else-if="joined && hasApprovedContent"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-green-600 font-bold text-sm"
      >
        👌 Thank you for submitting. You can now post on Social Media.
      </div>
      <div
        v-else-if="!joined && !applied && !campaignApplyByPassed"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-green-600 font-bold text-sm"
      >
        Apply to the campaign using the "Join campaign" button
      </div>
      <div
        v-else-if="!joined && !applied && campaignApplyByPassed"
        class="block mt-4 py-3 px-4 uppercase text-center text-white mb-4 bg-gray-600 font-bold text-sm"
      >
        Applications are closed
      </div>
    </div>
    <div v-if="joined">
      <div v-if="campaignHasUploadableDeliverables">
        <div v-if="canUploadContent">
          <div class="leading-tight mt-4">After you shoot with the products please upload the content here. 📸</div>
          <div v-if="hasMaximumContentSubmissionCount" class="leading-tight mt-4">
            <p>
              Please note that you have a maximum of three (3) opportunities to send the adequate content for this
              campaign. After first sending the content, if we consider that changes need to be made, you will have 2
              more tries to send revisions in order to successfully complete the task. The collaboration will get
              canceled automatically after the 3rd time if not accepted.
            </p>
            <p class="mt-4">
              You have
              <span class="font-semibold"> {{ leftRetriesContentSubmissionCount }} more {{ leftRetriesNoun }}</span>
              left to submit content for review.
            </p>
          </div>
          <div class="leading-tight mt-4 mb-1 text-sm">
            Maximum file size: <strong>200MB</strong>
            <br />
            Allowed file types: <strong v-if="campaignHasVideoDeliverables">.mpeg, .quicktime, .mp4</strong>
            <strong v-if="campaignHasPhotosDeliverables">.jpeg, .png, .jpg, .gif, .svg</strong>
          </div>
          <vue-dropzone
            id="campaign-application-dropzone"
            ref="campaignApplicationDropzone"
            class="h-full"
            :options="dropzoneOptions"
            @vdropzone-success="vdropzoneSuccess"
            @vdropzone-error="vdropzoneError"
            @vdropzone-mounted="loadComponent"
            @vdropzone-removed-file="vdropzoneFileRemoved"
          />
          <div class="text-sm mt-2">
            By uploading you agree with
            <a href="/terms" target="_blank">Terms and Conditions</a> and
            <a href="/privacy" target="_blank">Privacy Policy</a>.
          </div>
        </div>

        <div v-if="!canUploadContent" class="mt-1 flex flex-col justify-center items-center">
          <div v-for="content in uploadedContent" :key="content.media_id" class="mt-3">
            <img :src="content.thumb ? content.thumb : content.dataURL" class="h-48 w-48 object-contain" />
          </div>
        </div>
      </div>
      <div v-if="campaignShouldDeliver('youtube_video')" class="mt-4">
        <div v-if="!hasApprovedContent && !contentApprovalStatusIsPending">
          <div class="mt-4 sub-heading text-xl">Youtube Link</div>
          <div class="mt-4">
            <div class="text-sm">Insert the Youtube Link below and then hit the upload button:</div>
            <div class="flex items-center text-sm">
              <input
                v-model="temporaryYoutubeLink"
                type="text"
                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
              />
              <button v-if="canUploadContent" class="ml-1 button button-primary" @click="submitYoutubeLink">
                <svg-vue icon="upload" class="h-6 transform text-white" />
              </button>
            </div>
          </div>
        </div>
        <div v-if="hasPostedDeliverable('youtube_video')" class="mt-1 flex flex-col justify-center items-center">
          <div v-if="youtubeThumbnail" class="mt-3">
            <img :src="youtubeThumbnail" class="h-48 w-48 object-contain" />
          </div>
        </div>
      </div>
      <form v-if="joined && canUploadContent" @submit.prevent="submitContent">
        <button
          class="mt-4 w-full button button-primary"
          :class="isDisabledSubmitContent ? 'button-invisible cursor-not-allowed' : ''"
          type="submit"
          :disabled="isDisabledSubmitContent"
        >
          Submit content
        </button>
        <div v-if="isDisabledSubmitContent" class="text-sm mt-2 mb-4">
          You will be able to submit the content after you upload all the requirements.
        </div>
        <div v-if="!isDisabledSubmitContent" class="text-sm mt-2 mb-4">
          By clicking this, the Brand will be notified about your uploaded content.
        </div>
      </form>
    </div>
  </timeline-step>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import isString from 'lodash/isString';
import vue2Dropzone from 'vue2-dropzone';
import { Bus } from '../../bus';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { gtmEvent } from '../../services/gtm';
import { youtubeId } from '../../helpers';
import Pluralize from 'pluralize';

export default {
  components: {
    vueDropzone: vue2Dropzone,
  },
  props: ['step'],
  data: () => ({
    temporaryYoutubeLink: '',
    temporaryReelsLink: '',
    canDeleteFiles: true,
  }),
  computed: {
    ...mapState('timeline', [
      'campaign',
      'uploadedContent',
      'campaignApplication',
      'youtubeLink',
      'contentRejectionsCount',
    ]),
    ...mapGetters('timeline', [
      'joined',
      'applied',
      'requiredContentCount',
      'campaignShouldDeliver',
      'campaignHasCreativeAssetsObjective',
      'hasApprovedContent',
      'hasPostedDeliverable',
      'hasUploadedAllDeliverables',
      'campaignApplyByPassed',
    ]),
    dropzoneOptions() {
      if (!this.joined) {
        return {};
      }

      return {
        dictDefaultMessage: 'Drop or click here to upload',
        url: `/campaign-applications/${this.campaignApplication.id}/media`,
        thumbnailHeight: 150,
        timeout: 30 * 60 * 1000, // 30 min
        maxFilesize: 200,
        maxFiles: this.maximumFileUploads,
        headers: { 'X-CSRF-TOKEN': document.head.querySelector('[name=csrf-token][content]').content },
        addRemoveLinks: this.canUploadContent,
      };
    },
    maximumFileUploads() {
      return this.requiredContentCount + 4;
    },
    youtubeThumbnail() {
      const youtubeId = this.youtubeId(this.youtubeLink);
      if (!youtubeId) {
        return null;
      }

      return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    },
    campaignHasPhotosDeliverables() {
      if (!this.campaign || !this.campaign.deliverables) {
        return false;
      }

      if (
        this.campaign.deliverables.instagram_post.enabled ||
        this.campaign.deliverables.instagram_story.enabled ||
        this.campaign.deliverables.photo.enabled
      ) {
        return true;
      }

      return false;
    },
    campaignHasVideoDeliverables() {
      if (!this.campaign || !this.campaign.deliverables) {
        return false;
      }
      if (
        this.campaign.deliverables.tiktok_video.enabled ||
        this.campaign.deliverables.reels.enabled ||
        this.campaign.deliverables.video.enabled ||
        this.campaign.deliverables.igtv.enabled ||
        this.campaign.deliverables.youtube_shorts.enabled
      ) {
        return true;
      }

      return false;
    },
    campaignHasUploadableDeliverables() {
      return this.campaignHasPhotosDeliverables || this.campaignHasVideoDeliverables;
    },
    contentApprovalStatusIsPending() {
      return this.campaignApplication && this.campaignApplication.status === 'review_content';
    },
    canUploadContent() {
      return this.campaignApplication && this.campaignApplication.status === 'waiting_for_content';
    },
    isDisabledSubmitContent() {
      if (this.campaignShouldDeliver('youtube_video') && !this.youtubeLink) {
        return true;
      }

      if (this.campaignHasUploadableDeliverables && !this.uploadedContent.length) {
        return true;
      }

      return false;
    },
    hasMaximumContentSubmissionCount() {
      return this.campaign.objective === 'creative_assets';
    },
    leftRetriesContentSubmissionCount() {
      return 3 - (this.contentRejectionsCount ?? 0);
    },
    leftRetriesNoun() {
      return Pluralize('try', this.leftRetriesContentSubmissionCount);
    },
  },
  watch: {
    youtubeLink() {
      this.temporaryYoutubeLink = this.youtubeLink;
    },
  },
  methods: {
    ...mapMutations('timeline', [
      'setYoutubeLink',
      'markStepCompleted',
      'markStepActive',
      'addUploadedContent',
      'setCampaignApplication',
      'removeUploadedContent',
    ]),
    youtubeId,
    vdropzoneSuccess(file, response) {
      const { media_id } = response;
      // eslint-disable-next-line camelcase
      file.media_id = media_id;

      this.addUploadedContent(file);
    },
    async vdropzoneFileRemoved(file) {
      if (!this.canDeleteFiles) {
        return;
      }
      this.removeUploadedContent(file);
      await window.axios.delete(`/campaign-applications/${this.campaignApplication.id}/media/${file.media_id}`);
    },
    vdropzoneError(file, response) {
      if (isString(response)) {
        if (response === 'You can not upload any more files.') {
          Bus.$emit(
            'error',
            `The maximum number of files we expect you to upload for this campaign is ${this.maximumFileUploads}. Please send us the content you are most proud of.`,
          );
        } else {
          Bus.$emit('error', response);
        }

        return;
      }
      if (response.errors) {
        Object.values(response.errors).forEach((errorMessages) =>
          errorMessages.forEach((errorMessage) => Bus.$emit('error', errorMessage)),
        );
      } else if (response.message) {
        Bus.$emit('error', response.message);
      }
    },
    async submitYoutubeLink() {
      const { data } = await window.axios.post(`/campaign-applications/${this.campaignApplication.id}/youtube_link`, {
        url: this.temporaryYoutubeLink,
      });

      if (data.success) {
        this.setYoutubeLink(this.temporaryYoutubeLink);
      }
    },
    loadComponent() {
      for (let i = 0; i < this.uploadedContent.length; i += 1) {
        const file = {
          size: this.uploadedContent[i].size,
          name: this.uploadedContent[i].name,
          media_id: this.uploadedContent[i].media_id,
        };
        this.$refs.campaignApplicationDropzone.manuallyAddFile(file, this.uploadedContent[i].thumb);
      }
    },
    async submitContent() {
      gtmEvent('submit_content', {
        ga_category: 'campaign',
        ga_action: 'submit_content',
      });

      const { data } = await window.axios.post(`/campaign-applications/${this.campaignApplication.id}/submit`);

      if (data && data.success) {
        this.canDeleteFiles = false;
        this.setCampaignApplication(data.campaign_application);

        if (data.campaign_application.status === 'publish_content') {
          this.markStepCompleted(this.step.id);
          this.markStepActive('postContent');
        }
      }
    },
  },
};
</script>
